import { CarrierLabel } from '@boldpenguin/sdk';

declare module '@angular/forms' {
  interface FormControl {
    carrier: IFormControlEnrollment;
  }
  interface AbstractControl {
    carrier?: IFormControlEnrollment;
  }
}

export enum AppointmentTypes {
  Sub = 'sub',
  Direct = 'direct',
}

export interface IEnrollment {
  id: string;
  allow_modify_enrollment_for_access: boolean;
  allow_instant_enrollment: boolean;
  appointment_pending: boolean;
  appointment_type: AppointmentTypes | null;
  available_appointment_types: AppointmentTypes[];
  carrier_name: string;
  carrier_id: string;
  carrier_online: boolean;
  completed: boolean;
  active: boolean;
  carrier_attributes: ICarrierAttributes;
  carrier_labels: CarrierLabel[];
  carrier_portal_url: string | null;
  carrier_subdomain: string;
  deactivated_carrier_product_keys: string[];
  enrollment_attributes: IEnrollmentAttributes;
  logo_path: string;
  logo_path_full: string;
  relationship_type?: string;
  relationship_type_display?: string;
  relationship_type_other?: string;
  carrier_products?: string[];
  self_service_registration: boolean;
  serverErrorMessages?: string[];
  preselected: boolean;
  producer_schema: IEnrollmentProducerSchema;
  ineligibleMessage?: string;
}

export interface IAvailableEnrollment {
  name: string;
  carrier_id: string;
  carrier_subdomain: string;
  carrier_labels: CarrierLabel[];
  logo_url?: string;
  available_appointment_types: AppointmentTypes[];
  products: string[];
  carrier_online: boolean;
}

export interface IFormControlEnrollment {
  active?: boolean;
  allow_instant_enrollment?: boolean;
  available_appointment_types: AppointmentTypes[];
  carrier_attributes: ICarrierAttributes;
  /**
   * Enrollment ID (misnomer)
   */
  carrier_id: string;
  carrier_labels: CarrierLabel[];
  carrier_online: boolean;
  carrier_subdomain: string;
  logo_url: string;
  name: string;
  preselected?: boolean;
  products: string[];
  self_service_registration?: boolean;
}

export interface IEnrollmentAppointment {
  completed_at: null | string;
  created_at: string;
  enrollment_id: string;
  id: string;
  notes: null | string;
  request_type: string;
  status: string;
  tenant_id: string;
  updated_at: string;
  user_id: string;
}

export interface IEnrollmentProducerSchema {
  display_value: {
    placeholder: string;
    help_text: string;
  };
  /**
   * The various credential fields required by the agency
   */
  producer_fields: {
    [key: string]: IProducerField;
  }[];
}

export class EnrollmentProducerSchema implements IEnrollmentProducerSchema {
  display_value = {
    placeholder: '',
    help_text: '',
  };
  producer_fields = [];

  constructor(init?: Partial<IEnrollmentProducerSchema>) {
    Object.assign(this, init);
  }
}

export interface IProducerField {
  /**
   * Placeholder that should be shown for the input
   */
  placeholder: string;
  /**
   * Regex validation for the input
   */
  regex: RegExp;
  /**
   * Help text to display below the input
   */
  help_text: string;
  /**
   * Error text to display if the regex is not valid
   */
  error_text: string;
}

export interface ICarrierAttributes {
  producer_code?: string;
  api_key?: string;
  api_secret?: string;
  producer_email?: string;
  use_agent_details?: string;
  username?: string;
  password?: string;
  branch_code?: string;
  producer_first_name?: string;
  producer_last_name?: string;
  producer_phone?: string;
  contract_number?: string;
}

export interface IEnrollmentAttributes {
  producer_code?: IEnrollmentAttribute;
  api_key?: IEnrollmentAttribute;
  api_secret?: IEnrollmentAttribute;
  producer_email?: IEnrollmentAttribute;
  use_agent_details?: IEnrollmentAttribute;
  username?: IEnrollmentAttribute;
  password?: IEnrollmentAttribute;
  branch_code?: IEnrollmentAttribute;
  producer_first_name?: IEnrollmentAttribute;
  producer_last_name?: IEnrollmentAttribute;
  producer_phone?: IEnrollmentAttribute;
  contract_number?: IEnrollmentAttribute;
}

export interface IEnrollmentAttribute {
  error_text?: string;
  help_text?: string;
  placeholder?: string;
  regex?: string;
  value?: string | number | boolean | null;
}

export interface IEnrollmentWithProducts extends IEnrollment {
  products: { name: string }[];
}
